import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle,
  Clock,
  Database,
  FileText,
  Lock,
  Mail,
  RefreshCw,
  Shield,
  Sparkles,
  Trash2,
} from 'lucide-react';
import { Button } from '../components/ui/button';

const COMPANY_EMAIL = 'privacy@godigitalstep.com';
const COMPANY_NAME = 'Go Digital Step';
const LAST_UPDATED = 'May 26, 2026';

const DATA_CATEGORIES = [
  'Account profile and contact details',
  'Authentication sessions and security tokens',
  'Projects, reports, tasks, and approvals',
  'Connected integration OAuth tokens',
  'Notification and platform preferences',
  'Usage logs tied to your account',
];

const REQUEST_SECTIONS = [
  {
    id: 'request-options',
    title: 'Request Options',
    icon: Trash2,
    body: (
      <div className="grid md:grid-cols-2 gap-5">
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-rose-500 to-red-600 text-white text-sm font-bold flex items-center justify-center">
              1
            </span>
            <h3 className="font-heading font-semibold text-slate-900 dark:text-white">
              Delete from your account
            </h3>
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed mb-4">
            If you can still sign in, use account settings to start the fastest deletion workflow.
          </p>
          <ol className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
            {['Sign in to your dashboard.', 'Open Settings and choose Security.', 'Select Delete Account and follow the prompts.'].map((item) => (
              <li key={item} className="flex gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ol>
          <Button className="gds-btn-primary mt-5" asChild>
            <Link to="/login">
              Sign In
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </Button>
        </div>

        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 text-white text-sm font-bold flex items-center justify-center">
              2
            </span>
            <h3 className="font-heading font-semibold text-slate-900 dark:text-white">
              Email our privacy team
            </h3>
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed mb-4">
            Send the request from the email address associated with your GDS account so we can verify ownership.
          </p>
          <div className="rounded-lg bg-slate-50 dark:bg-slate-800/70 border border-slate-200 dark:border-slate-700 p-3 text-sm">
            <p className="font-medium text-slate-800 dark:text-slate-200">To: {COMPANY_EMAIL}</p>
            <p className="text-slate-500 dark:text-slate-400 mt-1">Subject: Data Deletion Request - [your email]</p>
          </div>
          <Button variant="outline" className="mt-5 rounded-full" asChild>
            <a href={`mailto:${COMPANY_EMAIL}?subject=Data Deletion Request`}>
              <Mail className="w-4 h-4 mr-2" />
              Open Email Client
            </a>
          </Button>
        </div>
      </div>
    ),
  },
  {
    id: 'what-we-delete',
    title: 'What We Delete',
    icon: Database,
    body: (
      <div className="grid sm:grid-cols-2 gap-3">
        {DATA_CATEGORIES.map((item) => (
          <div
            key={item}
            className="flex items-start gap-3 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-800 p-3"
          >
            <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
            <span className="text-sm text-slate-600 dark:text-slate-400">{item}</span>
          </div>
        ))}
      </div>
    ),
  },
  {
    id: 'timeline',
    title: 'Timeline and Retention',
    icon: Clock,
    body: (
      <div className="grid md:grid-cols-3 gap-4">
        {[
          ['Request received', 'We acknowledge the request and verify account ownership.'],
          ['Deletion processing', 'Account data, integrations, sessions, and related records are removed.'],
          ['Completion notice', 'We email confirmation when the request is complete.'],
        ].map(([title, description], index) => (
          <div key={title} className="rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 p-4">
            <span className="w-7 h-7 rounded-lg bg-amber-500 text-white text-xs font-bold flex items-center justify-center mb-3">
              {index + 1}
            </span>
            <h3 className="font-heading font-semibold text-slate-900 dark:text-white mb-1">{title}</h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{description}</p>
          </div>
        ))}
        <p className="md:col-span-3 text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
          We process deletion requests within <strong className="text-slate-800 dark:text-slate-200">30 days</strong>.
          Some records may be retained where required by law, such as financial or security records. Anonymized
          statistics that cannot identify you may be kept for analytics.
        </p>
      </div>
    ),
  },
  {
    id: 'privacy-support',
    title: 'Privacy Support',
    icon: Shield,
    body: (
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-5">
        <div>
          <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed max-w-2xl">
            For access, correction, deletion, or other privacy-rights requests, contact our privacy team. Include
            the account email and enough detail for us to locate the relevant account.
          </p>
          <p className="text-sm text-slate-500 dark:text-slate-500 mt-3">
            Read the full policy for more information about how GDS handles personal data.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button className="gds-btn-primary" asChild>
            <a href={`mailto:${COMPANY_EMAIL}?subject=Privacy Request`}>
              <Mail className="w-4 h-4 mr-2" />
              Contact Privacy
            </a>
          </Button>
          <Button variant="outline" className="rounded-full" asChild>
            <Link to="/privacy-policy">Privacy Policy</Link>
          </Button>
        </div>
      </div>
    ),
  },
];

const STATUS_STEPS = [
  ['Request received', 'Your deletion request has been captured with this confirmation code.', CheckCircle],
  ['Verification', 'We verify the account and review retention obligations before deletion.', Lock],
  ['Removal', 'Account data and connected records are removed from active systems.', RefreshCw],
  ['Confirmation', 'A completion email is sent after processing finishes.', Mail],
];

/**
 * Facebook / Meta data-deletion compliance page.
 *
 * Two modes:
 *   1. /data-deletion            -> Instructions for users to request deletion.
 *   2. /data-deletion?id=<code>  -> Status check for an in-flight deletion request.
 */
export default function DataDeletionPage() {
  const [searchParams] = useSearchParams();
  const confirmationCode = searchParams.get('id');
  const [activeId, setActiveId] = useState(REQUEST_SECTIONS[0].id);

  // When a confirmation code is present, the deletion request is "received".
  // A real-world app would hit an API endpoint to check the current status.
  const [status] = useState(confirmationCode ? 'received' : null);

  useEffect(() => {
    document.title = confirmationCode
      ? `Deletion Request ${confirmationCode} - ${COMPANY_NAME}`
      : `Data Deletion - ${COMPANY_NAME}`;
  }, [confirmationCode]);

  useEffect(() => {
    if (confirmationCode) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: '-20% 0px -70% 0px' }
    );

    REQUEST_SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [confirmationCode]);

  const scrollTo = (id) => {
    setActiveId(id);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950">
      <PublicNav />
      <Hero confirmationCode={confirmationCode} />

      <div className="bg-rose-50 dark:bg-rose-950/30 border-b border-rose-100 dark:border-rose-900">
        <div className="max-w-7xl mx-auto px-6 py-5">
          <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed max-w-3xl">
            {confirmationCode
              ? 'Use this page to check that your data deletion request was received and to understand the remaining processing steps.'
              : 'Use this page to request permanent deletion of personal data associated with your GDS account or a Meta-connected app login.'}
          </p>
        </div>
      </div>

      {confirmationCode ? (
        <StatusView code={confirmationCode} status={status} />
      ) : (
        <InstructionsView activeId={activeId} onSelect={scrollTo} />
      )}

      <footer className="py-8 px-6 border-t border-slate-200 dark:border-slate-800 text-center text-sm text-slate-600 dark:text-slate-400">
        <p>
          © 2026 GDS - Go Digital Step. All rights reserved. &nbsp;·&nbsp;
          <Link to="/privacy-policy" className="hover:text-indigo-600">Privacy Policy</Link>
          &nbsp;·&nbsp;
          <Link to="/terms-and-conditions" className="hover:text-indigo-600">Terms and Conditions</Link>
        </p>
      </footer>
    </div>
  );
}

function PublicNav() {
  return (
    <nav className="fixed top-0 left-0 right-0 z-50 gds-glass border-b border-slate-200/50 dark:border-slate-800/50">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <span className="font-heading font-bold text-xl">GDS</span>
        </Link>
        <div className="hidden md:flex items-center gap-8">
          <Link to="/privacy-policy" className="text-sm text-slate-600 hover:text-indigo-600 dark:text-slate-300 dark:hover:text-indigo-400 transition-colors font-medium">
            Privacy
          </Link>
          <Link to="/terms-and-conditions" className="text-sm text-slate-600 hover:text-indigo-600 dark:text-slate-300 dark:hover:text-indigo-400 transition-colors font-medium">
            Terms
          </Link>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="ghost" asChild>
            <Link to="/login">Sign In</Link>
          </Button>
          <Button className="gds-btn-primary hidden sm:inline-flex" asChild>
            <Link to="/register">Get Started</Link>
          </Button>
        </div>
      </div>
    </nav>
  );
}

function Hero({ confirmationCode }) {
  return (
    <div className="pt-16 bg-gradient-to-br from-rose-600 via-red-600 to-indigo-700">
      <div className="max-w-7xl mx-auto px-6 py-16 flex flex-col md:flex-row md:items-center justify-between gap-8">
        <div className="flex items-center gap-6">
          <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center shrink-0">
            {confirmationCode ? (
              <FileText className="w-7 h-7 text-white" />
            ) : (
              <Trash2 className="w-7 h-7 text-white" />
            )}
          </div>
          <div>
            <p className="text-rose-100 text-sm font-medium mb-1">Privacy Request</p>
            <h1 className="font-heading font-bold text-4xl text-white">
              {confirmationCode ? 'Deletion Request Status' : 'Data Deletion Request'}
            </h1>
            <p className="text-rose-100 text-sm mt-1">Last updated: {LAST_UPDATED}</p>
          </div>
        </div>
        <div className="rounded-2xl bg-white/10 border border-white/20 backdrop-blur px-5 py-4 text-white max-w-sm">
          <p className="text-xs uppercase tracking-wider text-rose-100 font-semibold mb-1">
            Standard response time
          </p>
          <p className="font-heading text-2xl font-bold">Within 30 days</p>
          <p className="text-sm text-rose-100 mt-1">
            Subject to required legal retention periods.
          </p>
        </div>
      </div>
    </div>
  );
}

function InstructionsView({ activeId, onSelect }) {
  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <div className="flex gap-12">
        <aside className="hidden lg:block w-64 shrink-0">
          <div className="sticky top-24">
            <p className="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-4">
              On This Page
            </p>
            <nav className="space-y-1">
              {REQUEST_SECTIONS.map(({ id, title }, idx) => (
                <button
                  key={id}
                  onClick={() => onSelect(id)}
                  className={`w-full text-left flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all ${
                    activeId === id
                      ? 'bg-rose-50 dark:bg-rose-950/50 text-rose-600 dark:text-rose-400 font-medium'
                      : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800/50'
                  }`}
                >
                  <span className={`w-5 h-5 rounded-md text-xs font-bold flex items-center justify-center shrink-0 ${
                    activeId === id ? 'bg-rose-600 text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
                  }`}>
                    {idx + 1}
                  </span>
                  <span className="truncate">{title}</span>
                </button>
              ))}
            </nav>
          </div>
        </aside>

        <main className="flex-1 min-w-0 space-y-6">
          {REQUEST_SECTIONS.map(({ id, title, icon: Icon, body }, idx) => (
            <section
              key={id}
              id={id}
              className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden scroll-mt-28"
            >
              <div className="flex items-center gap-4 px-6 py-4 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
                <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-rose-500 to-indigo-600 text-white text-xs font-bold flex items-center justify-center shrink-0">
                  {idx + 1}
                </span>
                <Icon className="w-5 h-5 text-rose-600 dark:text-rose-400 shrink-0" />
                <h2 className="font-heading font-semibold text-slate-900 dark:text-white">{title}</h2>
              </div>
              <div className="px-6 py-5">
                {body}
              </div>
            </section>
          ))}

          <div className="rounded-2xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
            <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
              Deletion is permanent after completion. Download any reports, invoices, or project exports you need
              before submitting your request.
            </p>
          </div>
        </main>
      </div>
    </div>
  );
}

function StatusView({ code, status }) {
  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <div className="grid lg:grid-cols-[360px_1fr] gap-8">
        <aside className="space-y-5">
          <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
            <div className="px-6 py-5 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                Confirmation
              </p>
              <h2 className="font-heading font-semibold text-slate-900 dark:text-white mt-1">
                Request Received
              </h2>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-2">Confirmation code</p>
                <code className="block font-mono text-sm bg-slate-100 dark:bg-slate-800 px-3 py-2 rounded-lg text-slate-800 dark:text-slate-200 break-all">
                  {code}
                </code>
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-2">Status</p>
                <StatusBadge status={status} />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-2">Expected time</p>
                <p className="text-sm text-slate-700 dark:text-slate-300">Up to 30 days</p>
              </div>
            </div>
          </div>

          <div className="rounded-2xl bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-900 p-5">
            <Shield className="w-5 h-5 text-indigo-600 dark:text-indigo-400 mb-3" />
            <p className="font-heading font-semibold text-slate-900 dark:text-white mb-2">
              Need help?
            </p>
            <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed mb-4">
              Include your confirmation code when contacting the privacy team.
            </p>
            <Button variant="outline" className="rounded-full bg-white/70 dark:bg-slate-900/70" asChild>
              <a href={`mailto:${COMPANY_EMAIL}?subject=Data Deletion ${code}`}>
                <Mail className="w-4 h-4 mr-2" />
                Email Privacy
              </a>
            </Button>
          </div>
        </aside>

        <main className="space-y-6">
          <section className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
            <div className="flex items-center gap-4 px-6 py-4 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
              <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-indigo-600 text-white flex items-center justify-center shrink-0">
                <CheckCircle className="w-4 h-4" />
              </span>
              <h2 className="font-heading font-semibold text-slate-900 dark:text-white">
                What Happens Next
              </h2>
            </div>
            <div className="p-6">
              <div className="grid md:grid-cols-2 gap-4">
                {STATUS_STEPS.map(([title, description, Icon], index) => (
                  <div
                    key={title}
                    className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/60 p-4"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        index === 0
                          ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-950 dark:text-emerald-400'
                          : 'bg-slate-200 text-slate-500 dark:bg-slate-700 dark:text-slate-300'
                      }`}>
                        <Icon className="w-4 h-4" />
                      </span>
                      <h3 className="font-heading font-semibold text-slate-900 dark:text-white">{title}</h3>
                    </div>
                    <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="rounded-2xl border border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/30 p-5">
            <p className="font-heading font-semibold text-slate-900 dark:text-white mb-2">
              Important retention note
            </p>
            <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
              Active subscriptions are cancelled before account deletion. Records required for legal, tax, security,
              or fraud-prevention purposes may be retained only as long as required.
            </p>
          </section>
        </main>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    received: { label: 'Request received', className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' },
    processing: { label: 'Processing', className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
    completed: { label: 'Completed', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
  };
  const { label, className } = map[status] ?? map.received;
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${className}`}>
      {label}
    </span>
  );
}
